import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShoppingCart, ChevronRight, Sparkles, Plus, Minus, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductRecommendations = ({ currentProductId, category, limit = 4 }) => {
  const navigate = useNavigate();
  const { addToCart, getItemQuantity, updateQuantity } = useCart();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        // Fetch products from the same category
        const response = await axios.get(`${API}/products`, {
          params: {
            category: category,
            limit: limit + 1 // Fetch one extra to filter out current product
          }
        });
        
        // Filter out the current product and limit results
        const products = (response.data || [])
          .filter(p => p.id !== currentProductId)
          .slice(0, limit);
        
        setRecommendations(products);
      } catch (error) {
        console.error('Error fetching recommendations:', error);
      } finally {
        setLoading(false);
      }
    };

    if (category) {
      fetchRecommendations();
    }
  }, [currentProductId, category, limit]);

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart!`);
  };

  const handleIncrement = (e, product) => {
    e.stopPropagation();
    const qty = getItemQuantity(product.id);
    if (qty >= product.stock) {
      toast.error(`Only ${product.stock} items available`);
      return;
    }
    addToCart(product);
  };

  const handleDecrement = (e, product) => {
    e.stopPropagation();
    const qty = getItemQuantity(product.id);
    updateQuantity(product.id, qty - 1);
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-6 w-48 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={`reco-skeleton-${i}`} className="bg-white rounded-xl p-4 animate-pulse">
              <div className="aspect-square bg-slate-200 rounded-lg mb-3" />
              <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-slate-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return null;
  }

  return (
    <div className="py-8 border-t border-slate-200" data-testid="product-recommendations">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" />
          <h2 className="text-lg md:text-xl font-outfit font-bold text-slate-900">
            You May Also Like
          </h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-[#0047AB] hover:bg-[#0047AB]/5"
          onClick={() => navigate(`/products?category=${category}`)}
          data-testid="view-more-recommendations"
        >
          View More
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {recommendations.map((product, index) => (
          <motion.div
            key={product.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -5 }}
            className="group cursor-pointer bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
            onClick={() => navigate(`/product/${product.id}`)}
            data-testid={`recommendation-${product.id}`}
          >
            {/* Image */}
            <div className="relative aspect-square bg-slate-50 overflow-hidden">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              
              {/* Discount Badge */}
              {product.original_price && product.original_price > product.price && (
                <div className="absolute top-2 right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                  {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
                </div>
              )}
            </div>

            {/* Details */}
            <div className="p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">
                {product.category}
              </p>
              <h3 className="font-outfit font-medium text-slate-900 text-sm line-clamp-1 mb-1">
                {product.name}
              </h3>
              
              {/* Price */}
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-[#0047AB]">
                  ₹{product.price.toLocaleString()}
                </span>
                {product.original_price && product.original_price > product.price && (
                  <span className="text-xs text-[#f57224] font-semibold">
                    {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
                  </span>
                )}
              </div>

              {/* Delivery Date */}
              <div className="flex items-center gap-1 mb-2 text-[#0047AB]">
                <Truck className="w-3 h-3" />
                <span className="text-[10px] font-medium">
                  Delivery by {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                </span>
              </div>

              {/* Add to Cart Button / Counter */}
              {(() => {
                const qty = getItemQuantity(product.id);
                return qty > 0 ? (
                  <div className="w-full flex items-center justify-center bg-[#0047AB] rounded-md h-8">
                    <button
                      onClick={(e) => handleDecrement(e, product)}
                      className="flex-1 h-full flex items-center justify-center text-white hover:bg-white/20 rounded-l-md transition-colors"
                      data-testid={`decrement-rec-${product.id}`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-8 text-center text-white font-bold text-sm">
                      {qty}
                    </span>
                    <button
                      onClick={(e) => handleIncrement(e, product)}
                      className="flex-1 h-full flex items-center justify-center text-white hover:bg-white/20 rounded-r-md transition-colors disabled:opacity-50"
                      disabled={qty >= product.stock}
                      data-testid={`increment-rec-${product.id}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    className="w-full bg-[#0047AB] hover:bg-[#003380] text-white text-xs h-8"
                    onClick={(e) => handleAddToCart(e, product)}
                    data-testid={`add-recommendation-${product.id}`}
                  >
                    <ShoppingCart className="w-3 h-3 mr-1" />
                    Add to Cart
                  </Button>
                );
              })()}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ProductRecommendations;
